import { useState } from 'react';
import { MessageSquare, X, Send, Minimize2, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    { id: 'welcome-msg', role: 'assistant', content: 'Hello! I\'m your DowUrk AI Guide. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [msgCounter, setMsgCounter] = useState(1);

  const quickActions = [
    { id: 'qa-businesses', text: 'Find businesses', icon: '🏢' },
    { id: 'qa-ai-help', text: 'Get AI help', icon: '🤖' },
    { id: 'qa-grants', text: 'Find grants', icon: '💰' }
  ];

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = input;
    const userMsgId = `user-${msgCounter}`;
    const assistantMsgId = `assistant-${msgCounter}`;
    setMsgCounter(prev => prev + 1);
    setInput('');
    setMessages(prev => [...prev, { id: userMsgId, role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        conversation_history: messages.slice(-6),
        context_type: 'general'
      });
      setMessages(prev => [...prev, { id: assistantMsgId, role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        id: assistantMsgId,
        role: 'assistant', 
        content: 'I\'m having trouble right now. Please try again or contact support.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action) => {
    setInput(action);
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-[132px] md:bottom-6 right-4 md:right-6 z-40">
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-full w-12 h-12 md:w-14 md:h-14 bg-white/70 backdrop-blur-md border border-white/40 hover:bg-white/80 shadow-lg"
        >
          <MessageSquare className="h-5 w-5 md:h-6 md:w-6 text-[#006847]" />
        </Button>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <div className="fixed bottom-[132px] md:bottom-6 right-4 md:right-6 z-40">
        <Button
          onClick={() => setIsMinimized(false)}
          className="rounded-full w-10 h-10 md:w-12 md:h-12 bg-white/70 backdrop-blur-md border border-white/40 hover:bg-white/80 shadow-lg"
        >
          <Maximize2 className="h-4 w-4 md:h-5 md:w-5 text-[#006847]" />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-[76px] md:bottom-6 right-4 md:right-6 w-[calc(100vw-2rem)] md:w-80 z-40 bg-white/70 backdrop-blur-md rounded-2xl shadow-xl border border-white/40">
      {/* Header */}
      <div className="p-3 bg-gradient-to-r from-[#A4D65E]/80 to-[#006847]/80 text-white flex items-center justify-between rounded-t-2xl">
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-4 w-4" />
          <span className="font-semibold text-sm">DowUrk AI</span>
        </div>
        <div className="flex items-center space-x-1">
          <button onClick={() => setIsMinimized(true)} className="hover:bg-white/20 p-1 rounded">
            <Minimize2 className="h-3 w-3" />
          </button>
          <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded">
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="h-64 overflow-y-auto p-3 space-y-2">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] p-2 rounded-lg text-xs ${
              msg.role === 'user' 
                ? 'bg-[#006847]/80 text-white' 
                : 'bg-white/60 text-gray-800 border border-gray-200'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white/60 border border-gray-200 p-2 rounded-lg text-xs">
              <span className="animate-pulse">Thinking...</span>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="px-3 pb-2 flex gap-1 flex-wrap">
        {quickActions.map((action) => (
          <button
            key={action.id}
            onClick={() => handleQuickAction(action.text)}
            className="text-xs px-2 py-1 bg-white/50 hover:bg-white/70 rounded-full border border-gray-200 transition"
          >
            {action.icon} {action.text}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-white/30">
        <div className="flex space-x-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Ask me anything..."
            className="text-xs bg-white/50 border-gray-200"
          />
          <Button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            size="sm"
            className="bg-[#006847]/80 hover:bg-[#006847] h-8 px-2"
          >
            <Send className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ChatbotWidget;